<template>
  <TransitionRoot
    as="template"
    :show="true">
    <Dialog
      as="div"
      class="relative z-50"
      @close="close">
      <TransitionChild
        as="template"
        enter="ease-in-out duration-500"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in-out duration-500"
        leave-from="opacity-100"
        leave-to="opacity-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-hidden">
        <div class="absolute inset-0 overflow-hidden">
          <div class="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
            <TransitionChild
              as="template"
              enter="transform transition ease-in-out duration-500 sm:duration-700"
              enter-from="translate-x-full"
              enter-to="translate-x-0"
              leave="transform transition ease-in-out duration-500 sm:duration-700"
              leave-from="translate-x-0"
              leave-to="translate-x-full">
              <DialogPanel class="pointer-events-auto relative w-96">
                <TransitionChild
                  as="template"
                  enter="ease-in-out duration-500"
                  enter-from="opacity-0"
                  enter-to="opacity-100"
                  leave="ease-in-out duration-500"
                  leave-from="opacity-100"
                  leave-to="opacity-0">
                  <div class="absolute left-0 top-0 -ml-8 flex pr-2 pt-4 sm:-ml-10 sm:pr-4">
                    <button
                      type="button"
                      class="relative rounded-md text-gray-300 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                      @click="close">
                      <span class="absolute -inset-2.5" />
                      <span class="sr-only">Close panel</span>
                      <svg
                        class="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                        aria-hidden="true">
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </TransitionChild>
                <div class="h-full overflow-y-auto bg-white p-8">
                  <div class="space-y-6 pb-16">
                    <div>
                      <media-item :file="media" />
                      <div class="mt-3 text-sm font-medium leading-6">
                        <TextEditableTextarea
                          v-if="fullMedia"
                          id="media-name"
                          v-model="fullMedia.name"
                          :text-area-class="[
                            'text-sm placeholder:text-gray-400 placeholder:italic',
                            {'text-gray-800 italic' : !fullMedia.created_at },
                          ]"
                          :auto-grow="false"
                          :tab-rows="3"
                          placeholder="Image text goes here.."
                          label="Image text"
                          label-class="text-gray-500 !font-normal"
                          :show-buttons="true"
                          :saved-changes="mediaUpdated"
                          @save="updateMedia" />
                        <h2
                          v-else
                          class="text-gray-900"
                          :class="{'italic' : !fullMedia.created_at }">
                          {{ media.name }}
                        </h2>
                        <p class="text-sm font-medium text-gray-500">
                          <formatted-size :size="media.size" />
                        </p>
                      </div>
                    </div>
                    <div
                      v-if="!fullMedia.created_at"
                      class="flex items-center justify-center">
                      <spinning-circle />
                    </div>
                    <template v-else>
                      <div>
                        <h3 class="font-medium text-gray-900">
                          Information
                        </h3>
                        <dl class="mt-2 divide-y divide-gray-200 border-y border-gray-200">
                          <div class="flex flex-col py-3 text-sm font-medium">
                            <dt class="text-gray-500">
                              Alt description
                            </dt>
                            <dd class="text-gray-900">
                              <TextEditableTextarea
                                v-if="fullMedia"
                                id="media-name"
                                v-model="fullMedia.custom_properties.alt_description"
                                text-area-class="text-sm placeholder:text-gray-400 placeholder:italic"
                                :auto-grow="false"
                                :tab-rows="2"
                                placeholder="type in image alt description here.."
                                :show-buttons="true"
                                :saved-changes="mediaUpdated"
                                @save="updateMedia" />
                            </dd>
                          </div>
                          <div class="flex justify-between py-3 text-sm font-medium">
                            <dt class="text-gray-500">
                              Credit
                            </dt>
                            <dd class="flex items-center space-x-2 text-gray-900">
                              <TextEditableTextarea
                                v-if="fullMedia"
                                id="media-name"
                                v-model="fullMedia.custom_properties.credit"
                                text-area-class="text-sm placeholder:text-gray-400 placeholder:italic text-right"
                                :auto-grow="false"
                                :tab-rows="1"
                                placeholder="type in whom to credit the photo here.."
                                :show-buttons="true"
                                :saved-changes="mediaUpdated"
                                @save="updateMedia" />
                            </dd>
                          </div>
                          <div class="flex justify-between py-3 text-sm font-medium">
                            <dt class="text-gray-500">
                              Uploaded by
                            </dt>
                            <dd class="flex items-center space-x-2 text-gray-900">
                              <img
                                class="inline-block h-6 w-6 rounded-full"
                                :src="fullMedia.creator.profile_photo_url"
                                :alt="fullMedia.creator.name">
                              <span>{{ fullMedia.creator.name }}</span>
                            </dd>
                          </div>
                          <div class="flex justify-between py-3 text-sm font-medium">
                            <dt class="text-gray-500">
                              Created
                            </dt>
                            <dd class="text-gray-900">
                              <formatted-time :date="fullMedia.created_at" />
                            </dd>
                          </div>
                          <div
                            v-if="fullMedia.editor"
                            class="flex justify-between py-3 text-sm font-medium">
                            <dt class="text-gray-500">
                              Last modified
                            </dt>
                            <dd class="relative text-gray-900">
                              <Tooltip>
                                <template #trigger>
                                  <formatted-time :date="fullMedia.updated_at" />
                                </template>
                                <template #content>
                                  <span class="flex items-center space-x-2 text-gray-100">
                                    <span>by</span>
                                    <img
                                      class="inline-block h-6 w-6 rounded-full"
                                      :src="fullMedia.creator.profile_photo_url"
                                      :alt="fullMedia.creator.name">
                                    <span>{{ fullMedia.creator.name }}</span>
                                  </span>
                                </template>
                              </Tooltip>
                            </dd>
                          </div>
                        </dl>
                      </div>
                      <div>
                        <h3 class="font-medium text-gray-900">
                          Connected to
                        </h3>
                        <ul
                          role="list"
                          class="mt-2 divide-y divide-gray-200 border-y border-gray-200">
                          <li class="flex items-center justify-between py-3">
                            <div class="flex items-center">
                              <img
                                :src="fullMedia.team.photo_url"
                                :alt="fullMedia.team.name"
                                class="h-8 w-8 rounded-full">
                              <p class="ml-4 text-sm font-medium text-gray-900">
                                {{ fullMedia.team.name }}
                              </p>
                            </div>
                          </li>
                          <li
                            :key="place.id"
                            v-for="place in fullMedia.places"
                            class="flex items-center justify-between py-3">
                            <div class="flex items-center">
                              <img
                                :src="place.image"
                                :alt="place.name"
                                class="mx-1 h-4 w-6">
                              <p class="ml-4 text-sm font-medium text-gray-900">
                                {{ place.name }}
                              </p>
                            </div>
                            <button
                              type="button"
                              class="ml-6 rounded-md bg-white text-sm font-medium text-indigo-600 hover:text-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                              @click="unlinkPlace(place)">
                              Remove<span class="sr-only"> {{ place.name }}</span>
                            </button>
                          </li>
                          <li
                            :key="proposal.id"
                            v-for="proposal in fullMedia.proposals"
                            class="flex items-center justify-between py-3">
                            <div class="flex items-center">
                              <span class="mx-1 h-6 w-6 rounded-full bg-purple-400 p-1" />
                              <p class="ml-4 text-sm font-medium text-gray-900">
                                {{ proposal.title }}
                              </p>
                            </div>
                            <button
                              type="button"
                              class="ml-6 rounded-md bg-white text-sm font-medium text-indigo-600 hover:text-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                              @click="unlinkProposal(proposal)">
                              Remove<span class="sr-only"> {{ proposal.name }}</span>
                            </button>
                          </li>
                          <li
                            :key="entity.id"
                            v-for="entity in fullMedia.entities"
                            class="flex items-center justify-between py-3">
                            <div class="flex items-center">
                              <span class="mx-1 h-6 w-6 rounded-full bg-green-400 p-1" />
                              <p class="ml-4 text-sm font-medium text-gray-900">
                                {{ entity.place?.name ?? entity.label ?? fullMedia.selectedElementTitle ?? '' }}
                              </p>
                            </div>
                            <button
                              type="button"
                              class="ml-6 rounded-md bg-white text-sm font-medium text-indigo-600 hover:text-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                              @click="unlinkEntity(entity)">
                              Remove<span class="sr-only"> {{ entity.place?.name ?? entity.label ?? '' }}</span>
                            </button>
                          </li>
                          <!--
                                              <li class="flex items-center justify-between py-2">
                                                  <button type="button" class="group -ml-1 flex items-center rounded-md bg-white p-1 focus:outline-none focus:ring-2 focus:ring-indigo-500">
                                                      <span class="flex h-8 w-8 items-center justify-center rounded-full border-2 border-dashed border-gray-300 text-gray-400">
                                                        <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                          <path d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z" />
                                                        </svg>
                                                      </span>
                                                      <span class="ml-4 text-sm font-medium text-indigo-600 group-hover:text-indigo-500">Share</span>
                                                  </button>
                                              </li>
                                              -->
                        </ul>
                      </div>
                      <!--
                                      <div>
                                          <h3 class="font-medium text-gray-900">Used by</h3>
                                          <ul role="list" class="mt-2 divide-y divide-gray-200 border-b border-t border-gray-200">
                                              <li class="flex items-center justify-between py-3">
                                                  <div class="flex items-center">
                                                      <img :src="fullMedia.team.photo_url" :alt="fullMedia.team.name" class="h-8 w-8 rounded-full" />
                                                      <p class="ml-4 text-sm font-medium text-gray-900">{{ fullMedia.team.name }}</p>
                                                  </div>
                                              </li>
                                          </ul>
                                      </div>
                                      -->
                      <div class="flex flex-col">
                        <div class="flex space-x-3">
                          <button
                            type="button"
                            class="flex-1 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm
                          hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                            @click="selectMedia">
                            Select
                          </button>
                          <button
                            type="button"
                            class="flex-1 rounded-md bg-white px-3 py-2 text-sm font-semibold text-red-900 shadow-sm ring-1 ring-inset ring-red-700 hover:bg-red-200"
                            @click="deleteMedia">
                            Delete
                          </button>
                        </div>
                        <div class="flex mt-3">
                          <button
                            type="button"
                            class="flex-1 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-400 hover:bg-gray-200"
                            @click="close">
                            Close the editor
                          </button>
                        </div>
                      </div>
                    </template>
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
// use normal <script> to declare options
</script>

<script setup>

import {
  Dialog, DialogPanel, TransitionChild, TransitionRoot,
} from '@headlessui/vue';
import {
  ref, onMounted, reactive,
} from 'vue';
import MediaItem from '@/Components/Media/MediaItem.vue';
import FormattedSize from '@/ComponentsDeprecated/FormattedSize.vue';
import SpinningCircle from '@/ComponentsDeprecated/Loaders/SpinningCircle.vue';
import FormattedTime from '@/ComponentsDeprecated/Time/FormattedTime.vue';
import Tooltip from '@/Components/Tooltip.vue';
import TextEditableTextarea from '@/Components/Forms/TextEditableTextarea.vue';
import { notify } from 'notiwind';

/* Props and Emits */

const props = defineProps({
  media: {
    type: Object,
    required: false,
  },
});

const emit = defineEmits(['close', 'selected', 'updated']);
const fullMedia = ref({
  name: 'A picture says more than a thousand words...',
  custom_properties: {
    credit: '',
    alt_description: '',
    description: '',
  },
  creator: {},
  created_at: '',
  updated_at: '',
  team: {},
  places: [],
  proposals: [],
  entities: [],
});
const mediaUpdated = ref(false);

/**
 *
 */
function close() {
  emit('close');
}

/**
 *
 */
function selectMedia() {
  emit('selected');
  emit('close');
}

/**
 *
 */
function updateMedia() {
  axios.put(route('api.media.update', { media: props.media.id }), {
    name: fullMedia.value.name,
    custom_properties: fullMedia.value.custom_properties,
  })
    .then(() => {
      mediaUpdated.value = true;
      emit('updated', fullMedia.value);
      notify({
        type: 'success',
        title: 'Media updated',
        text: 'The media was updated successfully',
      }, 4000);
    })
    .catch((err) => {
      notify({
        type: 'danger',
        title: 'Something went wrong',
        text: 'Could not save media',
      }, 4000);
      console.log(err);
    });
}

/**
 *
 */
function deleteMedia() {
  axios.delete(route('api.media.destroy', { media: props.media.id }))
    .then((response) => {
      if (response.data.success) {
        notify({
          type: 'success',
          title: 'Media deleted',
          text: 'The media was deleted successfully',
        }, 4000);
        emit('deleted', props.media.id);
        emit('close');
      } else {
        notify({
          type: 'danger',
          title: 'Media was not deleted',
          text: 'There was an error deleting the media',
        }, 4000);
      }
    })
    .catch((err) => {
      notify({
        type: 'danger',
        title: 'Something went wrong',
        text: 'Could not delete media',
      }, 4000);
      console.log(err.data.message);
    });
}

/**
 *
 * @param place
 */
function unlinkPlace(place) {
  axios.delete(route('api.place.media.destroy', {
    place: place.id, media: props.media.id,
  }))
    .then((response) => {
      fullMedia.value.places = fullMedia.value.places.filter((obj) => obj.id !== place.id);
      if (response.data) {
        notify({
          type: 'success',
          title: 'Media unlinked',
          text: 'The media connection was removed',
        }, 4000);
      }
    })
    .catch((err) => {
      notify({
        type: 'danger',
        title: 'Something went wrong',
        text: 'Could not remove media',
      }, 4000);
      console.log(err);
    });
}
/**
 *
 * @param proposal
 */
function unlinkProposal(proposal) {
  axios.delete(route('api.proposal.media.destroy', {
    proposal: proposal.id, media: props.media.id,
  }))
    .then((response) => {
      if (response.data) {
        fullMedia.value.proposals = fullMedia.value.proposals.filter((obj) => obj.id !== proposal.id);
        notify({
          type: 'success',
          title: 'Media unlinked',
          text: 'The media connection was removed',
        }, 4000);
      }
    })
    .catch((err) => {
      notify({
        type: 'danger',
        title: 'Something went wrong',
        text: 'Could not remove media',
      }, 4000);
      console.log(err);
    });
}

/**
 *
 * @param entity
 */
function unlinkEntity(entity) {
  axios.delete(route('api.entity.media.destroy', {
    placeEntity: entity.id, media: props.media.id,
  }))
    .then((response) => {
      fullMedia.value.entities = fullMedia.value.entities.filter((obj) => obj.id !== entity.id);
      if (response.data) {
        notify({
          type: 'success',
          title: 'Media unlinked',
          text: 'The media connection was removed',
        }, 4000);
      }
    })
    .catch((err) => {
      notify({
        type: 'danger',
        title: 'Something went wrong',
        text: 'Could not remove media',
      }, 4000);
      console.log(err);
    });
}

/**
 *
 */
function fetchFullMedia() {
  axios.get(route('api.media.show', { media: props.media.id }))
    .then((response) => {
      // Update fullMedia with response.data while preserving reactivity
      fullMedia.value = {
        ...fullMedia.value,
        ...response.data,
        custom_properties: {
          ...fullMedia.value.custom_properties,
          ...response.data.custom_properties,
        },
        selectedElementTitle: props.media.selectedElementTitle ?? '',
      };
    })
    .catch((err) => {
      notify({
        type: 'danger',
        title: 'Something went wrong',
        text: 'Could not find media',
      }, 4000);
      console.log(err);
      emit('close');
    });
}

onMounted(() => {
  fetchFullMedia();
});

</script>
